import React, { useState, useEffect } from "react";
import livebid from "../../assets/livbid.png"; // Your icon/image
import { derhumOrangeSign } from "../../utils/data"; // Your custom orange sign (฿ or so)
import CurrencyDisplay from "../common/CurrencyDisplay";

export default function CurrentBidBox({
  currentBid: propCurrentBid,
  onBid,
  onBidWithIncrement = () => {},
  isConnected = true,
  auctionLaneId,
  vehicleId,
  isPlacingBid = false,
  winningBid = null,
  startingBid = 0,
  bidIncrement = 10,
  isRedPulsing = false,
  isGreenPulsing = false,
  onResetManualBid = null,
  auctionStatus = "ongoing",
  showBidErrorMessage = false,
  bidErrorMessage = null,
  dataToDisplay = null,
  setShowBidErrorMessage = () => {},
}) {
  const [currentBid, setCurrentBid] = useState(parseInt(propCurrentBid));
  const [customBid, setCustomBid] = useState(parseInt(propCurrentBid));
  const [manualIncrement, setManualIncrement] = useState(0);

  const jumpBidButtons = [500, 1000, 2000, 5000, 10000, 15000, 20000];

  // Sync from parent when prop changes (but not when we locally update)
  useEffect(() => {
    setCurrentBid(parseInt(propCurrentBid));
    setCustomBid(parseInt(propCurrentBid));
  }, [propCurrentBid]);

  // Reset manual increment to next winning bid amount
  const resetManualIncrement = () => {
    if (winningBid) {
      const nextAmount = parseInt(winningBid.amount) + parseInt(bidIncrement);
      setManualIncrement(nextAmount);
    } else {
      setManualIncrement(parseInt(startingBid) + parseInt(bidIncrement) || 0);
    }
  };

  // Handle manual increment/decrement
  const handleIncrementManualBid = () => {
    setManualIncrement((prev) => prev + parseInt(bidIncrement));
  };

  const handleDecrementManualBid = () => {
    const minAmount = winningBid?.amount || startingBid || 0;
    setManualIncrement((prev) =>
      Math.max(prev - parseInt(bidIncrement), minAmount)
    );
  };

  const handleManualBidInputChange = (e) => {
    const value = parseFloat(e.target.value) || 0;
    setManualIncrement(value);
  };

  // Reset manual increment when winning bid changes
  useEffect(() => {
    resetManualIncrement();
    setShowBidErrorMessage(false);
  }, [winningBid, startingBid, bidIncrement]);

  // Expose reset function to parent component
  useEffect(() => {
    if (onResetManualBid) {
      onResetManualBid(resetManualIncrement);
    }
  }, [onResetManualBid]);

  // Handle placing a bid
  const handlePlaceBid = (bidValue) => {
    const newBid = currentBid + bidValue;
    onBid(newBid);
  };

  // CSS for pulsing animations and pause overlay
  const pulsingStyles = `
    @keyframes pulse-red {
      0% {
        background-color: #ffffff;
        border-color: #ffffff;
      }
      50% {
        background-color: #ffebee;
        border-color: #f44336;
      }
      100% {
        background-color: #ffffff;
        border-color: #ffffff;
      }
    }

    @keyframes pulse-green {
      0% {
        background-color: #ffffff;
        border-color: #ffffff;
      }
      50% {
        background-color:rgb(204, 240, 204);
        border-color: #4caf50;
      }
      100% {
        background-color: #ffffff;
        border-color: #ffffff;
      }
    }

    @keyframes fadeInUp {
      from {
        opacity: 0;
        transform: translateY(30px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    @keyframes pulse-warning {
      0% {
        transform: scale(1);
        opacity: 1;
      }
      50% {
        transform: scale(1.05);
        opacity: 0.8;
      }
      100% {
        transform: scale(1);
        opacity: 1;
      }
    }

    .pulse-red {
      animation: pulse-red 1.5s ease-in-out infinite;
      border: 2px solid;
      box-shadow: 0 0 5px rgba(244, 67, 54, 0.5);
    }

    .pulse-green {
      animation: pulse-green 1.5s ease-in-out infinite;
      border: 2px solid;
      box-shadow: 0 0 15px rgba(76, 175, 80, 0.5);
    }

    .pause-overlay {
      animation: fadeInUp 0.5s ease-out;
    }

    .pause-overlay .fa-pause-circle {
      animation: pulse-warning 2s ease-in-out infinite;
    }
    `;

  // Inject CSS styles for pulsing animations
  useEffect(() => {
    const styleElement = document.createElement("style");
    styleElement.textContent = pulsingStyles;
    document.head.appendChild(styleElement);

    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  const [nextWinningBidAmount, setNextWinningBidAmount] = useState(currentBid);
  useEffect(() => {
    // calculate next winning bid amount
    if (winningBid) {
      const nextWinningBidAmount =
        parseInt(currentBid) + parseInt(bidIncrement);
      setNextWinningBidAmount(nextWinningBidAmount);
    }
  }, [winningBid, currentBid]);

  return (
    <div
      className={`p-6 flex flex-col gap-6 h-[521px] select-none ${
        isRedPulsing ? "pulse-red" : isGreenPulsing ? "pulse-green" : "bg-white"
      }`}
    >
      {/* Current Bid Section */}
      <div className="flex items-center justify-start px-5 gap-2 h-[67px] bg-[rgba(245,245,245,1)] rounded-lg">
        <img
          src={livebid}
          alt="Placeholder"
          className="h-16 w-20 object-contain"
        />
        <div className="flex flex-col items-center !py-3">
          <div className="uppercase text-[16px] font-medium text-[rgba(21,21,41,1)] tracking-wide">
            Current Bid
          </div>
          <div className="flex items-center gap-1 text-[32px] font-bold text-[rgba(233,89,35,1)] tracking-wide leading-none">
            {/* Use the new CurrencyDisplay component */}
            <CurrencyDisplay amount={currentBid} size="4xl" />
          </div>
          {/* Connection Status Indicator */}
          {/* <div className="flex items-center gap-1 mt-1">
            <div
              className={`w-2 h-2 rounded-full ${
                isConnected ? "bg-green-500" : "bg-red-500"
              }`}
            ></div>
            <span
              className={`text-xs ${
                isConnected ? "text-green-600" : "text-red-600"
              }`}
            >
              {isConnected ? "Connected" : "Disconnected"}
            </span>
          </div> */}
        </div>
      </div>

      {/* Custom Bid Section */}
      <div className="flex flex-col gap-4">
        {/* Manual Bid Input */}
        <div className="flex flex-col gap-2">
          <label className="text-sm font-semibold text-gray-700">
            Manual Bid Amount
          </label>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="w-8 h-8 rounded bg-gray-200 hover:bg-gray-300 text-gray-700 font-bold transition disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleDecrementManualBid}
              disabled={
                !isConnected ||
                isPlacingBid ||
                auctionStatus === "scheduled" ||
                auctionStatus === "paused" ||
                manualIncrement <= (winningBid?.amount || startingBid || 0)
              }
              title="Decrease bid amount"
            >
              -
            </button>
            <input
              type="number"
              value={manualIncrement}
              onChange={handleManualBidInputChange}
              disabled={
                !isConnected ||
                isPlacingBid ||
                auctionStatus === "scheduled" ||
                auctionStatus === "paused"
              }
              className="flex-1 text-center font-bold border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-orange-500"
              min={winningBid?.amount || startingBid || 0}
              step={bidIncrement}
            />
            <button
              type="button"
              className="w-8 h-8 rounded bg-gray-200 hover:bg-gray-300 text-gray-700 font-bold transition disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleIncrementManualBid}
              disabled={
                !isConnected ||
                isPlacingBid ||
                auctionStatus === "scheduled" ||
                auctionStatus === "paused"
              }
              title="Increase bid amount"
            >
              +
            </button>
          </div>
        </div>

        {/* Place Bid Button */}
        <button
          type="button"
          className="w-full py-3 rounded bg-[rgba(241,90,41,1)] hover:bg-orange-800 text-white font-semibold transition disabled:cursor-not-allowed text-lg"
          onClick={() => onBid(manualIncrement)}
          disabled={
            !isConnected ||
            isPlacingBid ||
            auctionStatus === "scheduled" ||
            auctionStatus === "paused" ||
            manualIncrement <= (winningBid?.amount || startingBid || 0)
          }
        >
          {manualIncrement > currentBid + bidIncrement
            ? "Jump Bid"
            : "Place Bid"}{" "}
          <CurrencyDisplay
            amount={manualIncrement}
            size="sm"
            signOptions={{ width: "15", height: "15", color: "#ffffff" }}
          />
        </button>
      </div>

      {showBidErrorMessage && (
        <div className="bg-red-200 text-red-600 text-sm text-center p-2 rounded-md">
          {bidErrorMessage}
        </div>
      )}

      {/* Bid Jump Section
      <div className="border-t border-gray-200 pt-5 flex flex-col items-center gap-5">
        <div className="text-xs text-center md:text-[16px] font-semibold text-[rgba(19,19,19,1)] tracking-wider">
          Bid Jump
        </div>
        <div className="flex flex-col w-full gap-2">
          {jumpBidButtons.map((jumpBidValue) => (
            <button
              type="button"
              className="bg-[#181728] py-3 text-xs font-bold text-white hover:bg-orange-500 transition-colors duration-200 disabled:opacity-50"
              disabled={
                !isConnected ||
                isPlacingBid ||
                auctionStatus === "scheduled" ||
                auctionStatus === "paused"
              }
              onClick={() => onBidWithIncrement(jumpBidValue)}
            >
              Bid +
              <CurrencyDisplay
                amount={jumpBidValue}
                size="sm"
                className="text-white text-xs"
                signOptions={{
                  width: "15",
                  height: "15",
                }}
              />
            </button>
          ))}
        </div>
      </div> */}
    </div>
  );
}
